import {ReactNode, useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import SharedData from '../common/SharedData'
import Loader from '../common/Loader'
import Paginator from '../common/Paginator'
import {prepareFilters} from '../orders/core/_requests'
import {
  IOfferListFilters,
  IOfferModel,
  IOfferSummary,
  OFFER_SORT_OPTIONS,
  OfferSortKey,
  OfferStatusFilter,
} from './cors/_models'
import {getOffers, getOfferSummary} from './cors/_requests'
import OffersTableFilter from './offer-list/OffersTableFilter'
import OffersTable from './offer-list/OffersTable'
import SummaryStrip from './offer-list/SummaryStrip'
import OfferDrawer from './offer-detail/OfferDrawer'
import './offers-list.css'
import ShopScopeBanner from '../../components/ShopScopeBanner'

/**
 * "My Offers" page — the multi-supplier-aware replacement for the
 * legacy product list. Page state lives here:
 *   - search params drive filters + pagination (URL-shareable)
 *   - drawer is local state (open/close + which offer)
 *   - the summary counts are loaded here (strip + filter share them) and
 *     reloaded on every mutation via refreshKey — never on a filter change
 */

const PAGE_SIZE = 25

const readFilters = (sp: URLSearchParams): IOfferListFilters => ({
  page: Math.max(1, parseInt(sp.get('current_page') ?? '1', 10) || 1),
  limit: PAGE_SIZE,
  status: (sp.get('status') as OfferStatusFilter) || undefined,
  winning_only: sp.get('winning_only') === '1',
  not_winning: sp.get('not_winning') === '1',
  search: sp.get('search') ?? undefined,
  // Only forward a sort the backend recognizes (it also whitelists server-side);
  // keeps a junk ?sort= param out of the request entirely.
  sort: OFFER_SORT_OPTIONS.some((o) => o.value && o.value === sp.get('sort'))
    ? (sp.get('sort') as OfferSortKey)
    : undefined,
})

const OffersList = () => {
  const {formatMessage} = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const {currentUser, language} = SharedData()

  const [offers, setOffers] = useState<IOfferModel[]>([])
  const [totalResult, setTotalResult] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [drawerOffer, setDrawerOffer] = useState<IOfferModel | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [summary, setSummary] = useState<IOfferSummary | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await getOfferSummary(currentUser?.token, language)
        if (!cancelled) setSummary(res.data?.data ?? null)
      } catch {
        if (!cancelled) setSummary(null)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [currentUser?.token, language, refreshKey])

  const fetchOffers = useCallback(async () => {
    prepareFilters(searchParams, setSearchParams)
    setIsLoading(true)
    try {
      const filters = readFilters(searchParams)
      const res = await getOffers(filters, currentUser?.token, language)
      const body = res.data
      setOffers(body?.data ?? [])
      setTotalResult(body?.total ?? 0)
    } catch {
      setOffers([])
      setTotalResult(0)
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentUser?.token, language])

  useEffect(() => {
    void fetchOffers()
  }, [fetchOffers])

  const handleChanged = useCallback(async () => {
    setRefreshKey((k) => k + 1)
    await fetchOffers()
  }, [fetchOffers])

  const handleSaved = (updated: IOfferModel) => {
    // Merge (don't replace): the update response (getOwnedOffer) carries the
    // fresh price/cost/quantity/sku/status but NOT product_name or image, so a
    // straight replace blanked the row's name to "—". Overlay the updated
    // fields onto the existing row instead.
    setOffers((prev) =>
      prev.map((o) =>
        o.product_supplier_id === updated.product_supplier_id ? {...o, ...updated} : o,
      ),
    )
    setDrawerOffer(null)
    setRefreshKey((k) => k + 1)
  }

  const resultsBody: ReactNode = (
    <div className="hsb-of-card">
      {isLoading ? <Loader isFull /> : null}
      <OffersTable
        offers={offers}
        onEdit={(o) => setDrawerOffer(o)}
        onChanged={handleChanged}
      />
      <div className="hsb-of-pagination">
        <Paginator totalResult={totalResult} perPage={PAGE_SIZE} />
      </div>
    </div>
  )

  return (
    <div className="hsb-offers">
      <ShopScopeBanner />
      <div className="hsb-of-header">
        <h1 className="hsb-of-title">{formatMessage({id: 'offers.page_title'})}</h1>
      </div>

      <SummaryStrip data={summary} />

      <OffersTableFilter hasUnpricedOffers={(summary?.unpriced_offers ?? 0) > 0} />
      {resultsBody}

      <OfferDrawer
        offer={drawerOffer}
        onClose={() => setDrawerOffer(null)}
        onSaved={handleSaved}
      />
    </div>
  )
}

export default OffersList
